import React, { useState } from 'react';

import {
  Flex,
  Text,
  Center,
  ModalContent,
  Modal,
  ModalOverlay,
  HStack,
  ModalHeader,
  ModalBody,
  Stack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

import { Button, Carousel, Icon } from 'components';
import { InfoRow } from 'containers/Texts';
import { HistoryTypes } from 'enums/types';

interface HistoryRowProps {
  data: any
}

const historyIcons = {
  [HistoryTypes.Create]: { name: 'arrow-alt-circle-left', family: 'fal', color: 'gray.300' },
  [HistoryTypes.DeleteRoute]: { name: 'trash', family: 'fas', color: 'system.error.default' },
  [HistoryTypes.Disable]: { name: 'trash', family: 'fas', color: 'system.error.default' },
  [HistoryTypes.Execution]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.NewActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.StartActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.CompletedActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.CompletedRoute]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.CompletedOverdueActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.DispatchActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.TranshipmentActivity]: { name: 'star', family: 'fas', color: 'primary.default' },
  [HistoryTypes.Restore]: { name: 'trash-restore-alt', family: 'fas', color: 'system.ongoing.default' },
  [HistoryTypes.Update]: { name: 'edit', family: 'far', color: 'system.warn.default' },
  [HistoryTypes.UpdateActivity]: { name: 'edit', family: 'far', color: 'system.warn.default' },
  [HistoryTypes.CompletedOverdueActivity]: {
    name: 'star', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.Unproductive]: {
    name: 'exclamation', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.Occurrence]: {
    name: 'exclamation', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.RouteSyncError]: {
    name: 'bug', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.ActivitySyncError]: {
    name: 'bug', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.SyncError]: {
    name: 'bug', family: 'fas', color: 'system.error.default', px: 1,
  },
  [HistoryTypes.FreightsSyncError]: {
    name: 'bug', family: 'fas', color: 'system.error.default', px: 1,
  },
};
const userAction = {
  [HistoryTypes.Create]: 'Criada por',
  [HistoryTypes.DeleteRoute]: 'Removida por',
  [HistoryTypes.Disable]: 'Desabilitada por',
  [HistoryTypes.Execution]: 'Realizada por',
  [HistoryTypes.NewActivity]: 'Iniciada por',
  [HistoryTypes.StartActivity]: 'Iniciada por',
  [HistoryTypes.CompletedActivity]: 'Finalizada por',
  [HistoryTypes.CompletedOverdueActivity]: 'Finalizada por',
  [HistoryTypes.CompletedRoute]: 'Finalizada por',
  [HistoryTypes.DispatchActivity]: 'Realizada por',
  [HistoryTypes.TranshipmentActivity]: 'Realizada por',
  [HistoryTypes.Restore]: 'Reativada por',
  [HistoryTypes.Update]: 'Editada por',
  [HistoryTypes.UpdateActivity]: 'Editada por',
  [HistoryTypes.Unproductive]: 'Sinalizada por',
  [HistoryTypes.Occurrence]: 'Sinalizada por',
  [HistoryTypes.RouteSyncError]: 'Ocorreu com',
  [HistoryTypes.ActivitySyncError]: 'Ocorreu com',
  [HistoryTypes.SyncError]: 'Ocorreu com',
  [HistoryTypes.FreightsSyncError]: 'Ocorreu com',
};
function UnproductiveModal({
  isOpen, onClose, data, type,
}) {
  return (
    <Modal
      size="sm"
      isCentered
      isOpen={isOpen}
      onClose={onClose}>
      <ModalOverlay opacity={0} />
      <ModalContent size="sm">
        <ModalHeader
          borderBottom="2px solid"
          borderColor="gray.50">
          <HStack>
            <Center
              w="min"
              h="min"
              p={2}
              borderRadius={4}
              bg="system.error.transparent">
              <Icon
                size="2xl"
                name="exclamation-triangle"
                color="system.error.default"
              />
            </Center>
            <Text variant="xLargeSemiBold">{type}</Text>
          </HStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <Stack
            spacing={10}>
            <Stack>
              <Text variant="xLargeSemiBold">{data.reason}</Text>
              <Text variant="mediumRegular">{data.description}</Text>
            </Stack>
            <Carousel
              images={[...new Set(data.imageUrls)]}
            />
            <Button
              onClick={onClose}
              variant="primary.blue"><Text variant="largeBold">Ok</Text>
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default function HistoryRow({ data = {} }: HistoryRowProps) {
  const [info, setInfo] = useState<any>({});
  return (
    <Flex mb={2}>
      <UnproductiveModal
        data={info}
        type={data.type}
        isOpen={!isEmpty(info)}
        onClose={() => setInfo({})}
      />
      <Center
        p={1}
        h="min"
        mr={2}
        border="1px solid"
        borderColor={historyIcons[data.type]?.color}
        borderRadius={50}>
        <Icon
          size="2xs"
          {...historyIcons[data.type]}
        />
      </Center>
      <Flex flexDir="column">
        <Text variant="xSmallSemiBold">{data.type || 'N/A'}</Text>
        <InfoRow
          label={userAction[data.type]}
          value={data.user?.name || 'N/A'}
        />
        <Text
          variant="xSmallRegular"
          color="gray.400">{dayjs(data.createdAt).format('DD/MM/YYYY - HH:mm')}
        </Text>
        {[HistoryTypes.Unproductive, HistoryTypes.Occurrence].includes(data.type) && (
          <Text
            onClick={() => {
              if (data.type === HistoryTypes.Unproductive) {
                setInfo(data.unproductive);
              }
              if (data.type === HistoryTypes.Occurrence) {
                setInfo(data.occurrence);
              }
            }}
            cursor="pointer"
            variant="xSmallRegular"
            color="primary.default">Ver {data.type}
          </Text>
        )}
      </Flex>

    </Flex>
  );
}
